import { useAuth } from '@clerk/clerk-react';
import { Outlet, createRootRoute, useRouter } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import {
  endpointHealth2GET,
  endpointHealthGET,
} from '~/api/def/endpoints/health/healthEndpoint';
import { authAtom } from '~/atoms/authAtom';
import { InitialLoadingScreen } from '~/components/ui/misc/InitialLoadingScreen';
import { OrganizationInitializer } from '~/components/OrganizationInitializer';

export const ErrorComponent = () => {
  return (
    <div
      className="flex align-items-center justify-content-center"
      style={{ height: '100vh' }}
    >
      <div className="text-center">
        <h2>Something went wrong</h2>
        <p>Failed to load the application. Please try refreshing the page.</p>
      </div>
    </div>
  );
};

const retryWithTimeout = async (
  fn: () => Promise<void>,
  timeoutMs: number = 60000,
) => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeoutMs) {
    try {
      return await fn();
    } catch (error) {
      // If we still have time, wait a bit and retry
      if (Date.now() - startTime < timeoutMs) {
        await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait 2 seconds between retries
        continue;
      }
      throw error;
    }
  }
  throw new Error('Operation timed out after ' + timeoutMs + 'ms');
};

export const Route = createRootRoute({
  component: RootComponent,
});

function RootComponent() {
  const router = useRouter();
  const [, setAuthStore] = useAtom(authAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isRouteReady, setIsRouteReady] = useState(false);
  const auth = useAuth();

  // Update authStore when auth changes (including org changes)
  useEffect(() => {
    if (auth.isSignedIn && auth.isLoaded) {
      setAuthStore({ auth: auth });
    } else if (auth.isLoaded && !auth.isSignedIn) {
      setAuthStore(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isSignedIn, auth.isLoaded, auth.orgId, auth.userId, setAuthStore]);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await retryWithTimeout(async () => {
          await Promise.all([endpointHealthGET(), endpointHealth2GET()]);
        });
        setIsLoading(false);
      } catch (error) {
        console.error(
          'Health check failed after 60 seconds of retrying:',
          error,
        );
        setError(error as Error);
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Track route loading state
  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      setIsRouteReady(true);
    });

    return () => {
      unsubscribe();
    };
  }, [router]);

  if (isLoading || !isRouteReady || !auth.isLoaded) {
    return <InitialLoadingScreen />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <OrganizationInitializer>
      <Outlet />
    </OrganizationInitializer>
  );
}
